import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Coins, Loader2, Megaphone, Pencil, Plus, RotateCcw, Send, Settings2, ShoppingCart, Trash2, TrendingUp, Trophy, Zap } from "lucide-react";
import { botDelete, botGet, botPost, botPut, apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { InfoTip } from "@/pages/tickets/ticketsKit";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChannelSelect, RoleSelect } from "@/components/pickers";
import {
  ChannelMulti, Empty, FeatureGate, Field, Loading, MessageMini, NumberInput, PageHeader, Panel, PillTabs, RoleMulti, SaveBar, SimpleSelect,
  TextInput, ToggleRow, cleanMessage, dangerBtn, outlineBtn, primaryBtn, useFeature,
} from "@/components/kit";

const MEDAL = ["🥇", "🥈", "🥉"];

function RankRow({ u, children, onEdit }) {
  return (
    <div className="flex items-center gap-3 bg-[#131620] px-4 py-3 transition-colors hover:bg-[#1a1e2b]">
      <span className="w-8 text-center text-sm font-bold text-[#949BA4]">{MEDAL[u.rank - 1] ?? `#${u.rank}`}</span>
      {u.avatarUrl ? <img src={u.avatarUrl} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="h-9 w-9 rounded-full bg-[#232838]" />}
      <div className="min-w-0 flex-1"><p className="font-semibold text-white">{u.username}</p>{children}</div>
      <Button size="icon" variant="ghost" onClick={onEdit} className="text-[#949BA4] hover:bg-white/5 hover:text-white"><Pencil className="h-4 w-4" /></Button>
    </div>
  );
}

function EditValue({ open, title, label, value, suffix, onClose, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value, open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
        <DialogHeader><DialogTitle className="text-white">{title}</DialogTitle><DialogDescription className="text-[#949BA4]">Il cambiamento è immediato.</DialogDescription></DialogHeader>
        <Field label={label}><NumberInput value={v} max={1e12} className="w-40" suffix={suffix} onChange={setV} /></Field>
        <DialogFooter><Button onClick={() => onSave(v)} className={primaryBtn}>Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Modifica i punti di un membro, oppure imposta direttamente il livello. */
function EditXp({ user, onClose, onSaved }) {
  const [mode, setMode] = useState("level");
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(0);
  useEffect(() => { if (user) { setXp(user.xp); setLevel(user.level); } }, [user]);
  const save = async () => {
    try { await botPut(`levels/users/${user.userId}`, mode === "level" ? { level } : { xp }); toast.success("Aggiornato"); onSaved(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
        <DialogHeader><DialogTitle className="text-white">Livello di {user?.username}</DialogTitle><DialogDescription className="text-[#949BA4]">Il cambiamento è immediato; i ruoli premio vengono sistemati da soli.</DialogDescription></DialogHeader>
        <div className="flex w-fit gap-1 rounded-xl bg-[#131620] p-1">
          {[["level", "Imposta il livello"], ["xp", "Imposta i punti"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${mode === k ? "bg-[#5865F2] text-white" : "text-[#949BA4] hover:text-white"}`}>{l}</button>
          ))}
        </div>
        {mode === "level"
          ? <Field label="Livello"><NumberInput value={level} max={1000} className="w-28" onChange={setLevel} /></Field>
          : <Field label="Punti totali"><NumberInput value={xp} max={1e10} className="w-40" suffix="punti" onChange={setXp} /></Field>}
        <DialogFooter><Button onClick={save} className={primaryBtn}>Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Livelli ───────────────────────────
export default function Levels() {
  const [tab, setTab] = useState("board");
  return (
    <div className="space-y-6">
      <PageHeader icon={Trophy} title="Livelli" subtitle="Esperienza per messaggi e tempo in vocale, classifica e ruoli premio." />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "board", label: "Classifica", icon: Trophy }, { id: "settings", label: "Impostazioni", icon: Settings2 }]} />
      {tab === "board" ? <XpBoard /> : <LevelSettings />}
    </div>
  );
}

function XpBoard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reset, setReset] = useState(false);
  const load = useCallback(() => botGet("levels/leaderboard", { limit: 50 }).then(setData).catch((e) => { toast.error(apiError(e)); setData({ users: [] }); }), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      {data.users.length === 0 ? <Empty icon={Trophy} title="Classifica vuota">Attiva i livelli: i membri guadagnano XP scrivendo e stando in vocale.</Empty> : (
        <div className="divide-y divide-[#232838] overflow-hidden rounded-2xl border border-[#232838]">
          {data.users.map((u) => (
            <RankRow key={u.userId} u={u} onEdit={() => setEditing(u)}>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#5865F2]/20 px-2 py-0.5 text-[11px] font-bold text-[#a5b0ff]">Livello {u.level}</span>
                <div className="h-1.5 w-40 overflow-hidden rounded-full bg-[#232838]"><div className="h-full rounded-full bg-gradient-to-r from-[#5865F2] to-[#00F0FF]" style={{ width: `${Math.round((u.current / u.needed) * 100)}%` }} /></div>
                <span className="text-[11px] text-[#949BA4]">{u.xp.toLocaleString("it-IT")} XP · {u.messages} messaggi · {u.voiceMinutes ?? 0} min in vocale</span>
              </div>
            </RankRow>
          ))}
        </div>
      )}
      <Button variant="outline" onClick={() => setReset(true)} className="border-[#F23F43]/40 bg-transparent text-[#ff7a7d] hover:bg-[#F23F43]/10"><RotateCcw className="mr-2 h-4 w-4" />Azzera classifica</Button>
      <EditXp user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <Dialog open={reset} onOpenChange={setReset}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">Azzerare la classifica?</DialogTitle><DialogDescription className="text-[#949BA4]">Tutti tornano a 0 XP. Non si può annullare.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setReset(false)} className={outlineBtn}>Annulla</Button>
            <Button className={dangerBtn} onClick={async () => { try { await botDelete("levels"); toast.success("Classifica azzerata"); setReset(false); load(); } catch (e) { toast.error(apiError(e)); } }}>Azzera</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── punti per livello: stesso calcolo del bot (community/levels.js), per l'anteprima dal vivo ──
const CURVE_DEF = { mode: "formula", difficulty: 1, milestones: [], maxLevel: 0 };
const xpToNext = (l) => 5 * l * l + 50 * l + 100;
export function curveTable(curve, upTo = 100) {
  const c = { ...CURVE_DEF, ...(curve || {}) };
  const top = Math.min(upTo, c.maxLevel > 0 ? c.maxLevel : 1000);
  const t = [0];
  if (c.mode === "custom" && c.milestones.length) {
    const pts = [{ level: 0, xp: 0 }, ...c.milestones.filter((m) => m.level >= 1).sort((x, y) => x.level - y.level)];
    for (let L = 1; L <= top; L++) {
      const next = pts.findIndex((q) => q.level >= L);
      if (next === -1) {
        const p1 = pts[pts.length - 2] ?? pts[0], p2 = pts[pts.length - 1];
        const step = Math.max(1, (p2.xp - p1.xp) / Math.max(1, p2.level - p1.level));
        t[L] = Math.round(p2.xp + step * (L - p2.level));
      } else {
        const p1 = pts[next - 1], p2 = pts[next];
        t[L] = Math.round(p1.xp + ((p2.xp - p1.xp) * (L - p1.level)) / Math.max(1, p2.level - p1.level));
      }
      if (t[L] <= t[L - 1]) t[L] = t[L - 1] + 1;
    }
  } else {
    const d = c.difficulty > 0 ? c.difficulty : 1;
    for (let L = 1; L <= top; L++) t[L] = t[L - 1] + Math.max(1, Math.round(xpToNext(L - 1) * d));
  }
  return t;
}
const nf = (n) => Number(n).toLocaleString("it-IT");
const DIFFICULTY = [
  { value: 0.5, label: "Facile (metà dei punti)" }, { value: 1, label: "Normale (come MEE6)" }, { value: 1.5, label: "Un po' più difficile (×1,5)" },
  { value: 2, label: "Difficile (doppio)" }, { value: 3, label: "Molto difficile (triplo)" }, { value: 5, label: "Estrema (×5)" },
];

function Info({ text }) { return <InfoTip text={text} />; }
const Label = ({ children, info }) => <span className="inline-flex items-center gap-1.5">{children}{info && <Info text={info} />}</span>;

/** Editor della tabella «livello → punti totali». */
function CustomCurve({ curve, onChange, avgXp }) {
  const rows = [...(curve.milestones || [])].sort((x, y) => x.level - y.level);
  const set = (milestones) => onChange({ ...curve, milestones });
  const [gen, setGen] = useState({ to: 50, first: 100, plus: 50 });
  const t = curveTable(curve, Math.max(10, rows.at(-1)?.level ?? 10));
  const add = () => {
    const last = rows.at(-1);
    const prev = rows.at(-2);
    const step = last ? Math.max(1, last.xp - (prev?.xp ?? 0)) : 100;
    set([...rows, { level: (last?.level ?? 0) + 1, xp: (last?.xp ?? 0) + step }]);
  };
  const generate = () => {
    const out = [];
    let total = 0;
    for (let L = 1; L <= Math.min(1000, gen.to); L++) { total += gen.first + gen.plus * (L - 1); out.push({ level: L, xp: total }); }
    set(out);
    toast.success(`Creati ${out.length} livelli`, { description: "Puoi cambiare a mano ogni riga." });
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/[0.07] p-3">
        <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-white">✨ Riempi in automatico<Info text="Crea in un colpo tutti i livelli: il livello 1 costa i punti che scrivi, e ogni livello dopo costa un po' di più. Poi puoi cambiare a mano qualsiasi riga." /></p>
        <div className="flex flex-wrap items-center gap-2 text-sm text-[#DBDEE1]">
          Fino al livello <NumberInput value={gen.to} min={1} max={1000} className="w-20" onChange={(to) => setGen({ ...gen, to })} />
          · il livello 1 costa <NumberInput value={gen.first} min={1} max={1e9} className="w-24" onChange={(first) => setGen({ ...gen, first })} />
          punti · ogni livello dopo <NumberInput value={gen.plus} min={0} max={1e9} className="w-24" onChange={(plus) => setGen({ ...gen, plus })} /> in più
          <Button size="sm" onClick={generate} className={primaryBtn}>Crea la tabella</Button>
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-[#232838]">
        <div className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_44px] gap-2 border-b border-[#232838] bg-[#12141d] px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-[#6b7185]">
          <span>Livello</span><span className="flex items-center gap-1">Punti totali<Info text="I punti che servono in tutto per arrivare a questo livello. Esempio: livello 10 → 10.000 vuol dire che chi ha 10.000 punti è livello 10." /></span><span>Da fare dal precedente</span><span>Circa messaggi</span><span />
        </div>
        <div className="max-h-96 overflow-y-auto">
          {rows.length === 0 && <p className="p-4 text-center text-sm text-[#949BA4]">Nessun livello: aggiungine uno o usa «Riempi in automatico».</p>}
          {rows.map((r, i) => {
            const prevXp = r.level > 1 ? t[r.level - 1] ?? 0 : 0;
            return (
              <div key={i} className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_44px] items-center gap-2 border-b border-[#1b1f2e] px-3 py-1.5 last:border-0">
                <NumberInput value={r.level} min={1} max={1000} className="w-20" onChange={(level) => set(rows.map((x, j) => (j === i ? { ...x, level } : x)))} />
                <NumberInput value={r.xp} min={0} max={1e10} className="w-full max-w-[160px]" onChange={(xp) => set(rows.map((x, j) => (j === i ? { ...x, xp } : x)))} />
                <span className="text-sm text-[#B5BAC1]">{nf(Math.max(0, r.xp - prevXp))}</span>
                <span className="text-sm text-[#949BA4]">≈ {nf(Math.ceil(r.xp / Math.max(1, avgXp)))}</span>
                <Button size="icon" variant="ghost" onClick={() => set(rows.filter((_, j) => j !== i))} className="h-8 w-8 text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={add} className={outlineBtn}><Plus className="mr-1 h-4 w-4" />Aggiungi livello</Button>
        {rows.length > 0 && <Button size="sm" variant="ghost" onClick={() => set([])} className="text-[#ff7a7d] hover:bg-[#F23F43]/10">Svuota tabella</Button>}
        <p className="text-xs text-[#949BA4]">Non serve scrivere tutti i livelli: quelli che mancano vengono calcolati in mezzo (es. scrivi solo «10 → 10.000» e il livello 5 sarà a 5.000).</p>
      </div>
    </div>
  );
}

function LevelSettings() {
  const f = useFeature("levels");
  const [testing, setTesting] = useState(false);
  // Se cambiano i punti dei livelli, dopo il salvataggio ricalcola il livello di chi ha già punti (senza annunci).
  const savedCurve = useRef(null);
  useEffect(() => {
    if (!f.value || f.dirty) return;
    const key = JSON.stringify(f.value.curve ?? null);
    if (savedCurve.current !== null && savedCurve.current !== key) {
      botPost("levels/recalculate", {}).then((r) => r.changed && toast.success(`Livelli ricalcolati per ${r.changed} membri`)).catch(() => {});
    }
    savedCurve.current = key;
  }, [f.value, f.dirty]);

  const test = async () => {
    if (f.dirty) return toast.error("Salva prima le modifiche");
    setTesting(true);
    try { const r = await botPost("levels/test", {}); toast.success(`Annuncio di prova inviato (livello ${r.level})`); } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };

  return (
    <FeatureGate feature={f}>
      {(v) => {
        const curve = { ...CURVE_DEF, ...(v.curve || {}) };
        const setCurve = (p) => f.change({ curve: { ...curve, ...p } });
        const avgXp = Math.max(1, (v.xpMin + Math.max(v.xpMax, v.xpMin)) / 2);
        const preview = curveTable(curve, 20).slice(1);
        const msgsFor1 = Math.ceil((preview[0] ?? 0) / avgXp);
        return (
          <div className="space-y-4">
            <ToggleRow title={v.enabled ? "Livelli attivi" : "Livelli disattivati"} desc="I membri guadagnano esperienza e salgono di livello." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} testId="levels-toggle" />

            {/* ── punti per livello ── */}
            <Panel title="Punti necessari per ogni livello" icon={TrendingUp} desc="Decidi tu quanto è difficile salire di livello.">
              <div className="mb-4 grid gap-3 sm:grid-cols-2">
                {[["formula", "Automatica", "Ogni livello costa un po' più del precedente, come MEE6. Scegli solo la difficoltà."], ["custom", "Decido io", "Scrivi tu quanti punti servono per ogni livello (es. 10.000 punti = livello 10)."]].map(([k, t, d]) => (
                  <button key={k} type="button" onClick={() => setCurve({ mode: k })}
                    className={`rounded-xl border p-3 text-left transition-colors ${curve.mode === k ? "border-[#5865F2] bg-[#5865F2]/10" : "border-[#232838] bg-[#131620] hover:border-[#3a4160]"}`}>
                    <p className="font-semibold text-white">{curve.mode === k ? "● " : "○ "}{t}</p><p className="text-xs text-[#949BA4]">{d}</p>
                  </button>
                ))}
              </div>
              {curve.mode === "formula" ? (
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={<Label info="Moltiplica i punti di tutti i livelli. «Normale» è la formula di MEE6: livello 1 = 100 punti, livello 10 = 7.655, livello 50 = 268.275.">Difficoltà</Label>}>
                    <SimpleSelect value={DIFFICULTY.some((d) => d.value === curve.difficulty) ? curve.difficulty : "x"} onChange={(x) => x !== "x" && setCurve({ difficulty: Number(x) })}
                      options={[...DIFFICULTY, ...(DIFFICULTY.some((d) => d.value === curve.difficulty) ? [] : [{ value: "x", label: `Personalizzata (×${curve.difficulty})` }])]} />
                  </Field>
                  <Field label={<Label info="Oltre questo livello non si sale più (i punti continuano a contare in classifica). 0 = nessun limite.">Livello massimo</Label>}>
                    <NumberInput value={curve.maxLevel} min={0} max={1000} className="w-24" suffix="(0 = nessuno)" onChange={(maxLevel) => setCurve({ maxLevel })} />
                  </Field>
                </div>
              ) : (
                <div className="space-y-4">
                  <CustomCurve curve={curve} onChange={(c) => f.change({ curve: c })} avgXp={avgXp} />
                  <Field label={<Label info="Oltre questo livello non si sale più. 0 = nessun limite (dopo l'ultima riga si continua con lo stesso passo).">Livello massimo</Label>}>
                    <NumberInput value={curve.maxLevel} min={0} max={1000} className="w-24" suffix="(0 = nessuno)" onChange={(maxLevel) => setCurve({ maxLevel })} />
                  </Field>
                </div>
              )}
              <div className="mt-4">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7185]">Anteprima dei primi livelli<Info text="Punti totali per arrivare a ogni livello, e quanti messaggi servono più o meno con i punti per messaggio scelti qui sotto." /></p>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-5">
                  {preview.map((xp, i) => (
                    <div key={i} className="rounded-lg bg-[#131620] px-2.5 py-1.5 text-xs">
                      <span className="font-bold text-white">Liv. {i + 1}</span> <span className="text-[#B5BAC1]">{nf(xp)} pt</span>
                      <span className="block text-[10px] text-[#6b7185]">≈ {nf(Math.ceil(xp / avgXp))} messaggi</span>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>

            {/* ── come si guadagnano ── */}
            <Panel title="Come si guadagnano i punti" icon={Trophy} desc={`Con queste impostazioni per il livello 1 servono circa ${nf(msgsFor1)} messaggi.`}>
              <div className="space-y-3 text-sm text-[#DBDEE1]">
                <div className="flex flex-wrap items-center gap-2">Per messaggio da <NumberInput value={v.xpMin} max={1000} className="w-20" onChange={(xpMin) => f.change({ xpMin })} /> a <NumberInput value={v.xpMax} max={1000} className="w-20" suffix="punti" onChange={(xpMax) => f.change({ xpMax })} /><Info text="Ogni messaggio dà un numero di punti a caso tra questi due valori. Metti lo stesso numero per avere sempre gli stessi punti." /></div>
                <div className="flex flex-wrap items-center gap-2">Al massimo una volta ogni <NumberInput value={v.cooldownSeconds} max={3600} className="w-20" suffix="secondi" onChange={(cooldownSeconds) => f.change({ cooldownSeconds })} /><Info text="Anti-spam: i messaggi scritti prima di questo tempo dal precedente non danno punti." /></div>
                <div className="flex flex-wrap items-center gap-2">Messaggi più corti di <NumberInput value={v.minMessageLength ?? 0} max={500} className="w-20" suffix="caratteri non danno punti (0 = tutti valgono)" onChange={(minMessageLength) => f.change({ minMessageLength })} /></div>
                <div className="flex flex-wrap items-center gap-2">In vocale <NumberInput value={v.voiceXpPerMinute} max={1000} className="w-20" suffix="punti al minuto (0 = niente)" onChange={(voiceXpPerMinute) => f.change({ voiceXpPerMinute })} /></div>
                {v.voiceXpPerMinute > 0 && (
                  <div className="flex flex-wrap items-center gap-2 pl-4 text-[#B5BAC1]">… solo se nel canale ci sono almeno <NumberInput value={v.voiceMinMembers ?? 2} min={1} max={25} className="w-16" suffix="persone" onChange={(voiceMinMembers) => f.change({ voiceMinMembers })} />
                    <label className="ml-2 flex items-center gap-2"><Switch checked={!!v.voiceRequireUnmuted} onCheckedChange={(voiceRequireUnmuted) => f.change({ voiceRequireUnmuted })} />e con il microfono acceso</label></div>
                )}
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label={<Label info="In questi canali (o categorie) non si guadagnano punti, es. canali spam o bot.">Canali senza punti</Label>}><ChannelMulti value={v.noXpChannelIds} onChange={(noXpChannelIds) => f.change({ noXpChannelIds })} /></Field>
                <Field label={<Label info="Chi ha uno di questi ruoli non guadagna punti.">Ruoli senza punti</Label>}><RoleMulti value={v.noXpRoleIds} onChange={(noXpRoleIds) => f.change({ noXpRoleIds })} /></Field>
              </div>
            </Panel>

            {/* ── moltiplicatori ── */}
            <Panel title="Bonus (moltiplicatori)" icon={Zap} desc="Più punti per certi ruoli (es. booster ×2) o certi canali.">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-2">
                  <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Per ruolo<Info text="Chi ha il ruolo prende i punti moltiplicati. Se ne ha più di uno vale il bonus più alto." /></p>
                  {v.multipliers.map((m, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="min-w-0 flex-1"><RoleSelect value={m.roleId} onChange={(roleId) => f.change({ multipliers: v.multipliers.map((x, j) => (j === i ? { ...x, roleId } : x)) })} /></div>
                      ×<NumberInput value={m.multiplier} min={0} max={10} step={0.5} className="w-20" onChange={(multiplier) => f.change({ multipliers: v.multipliers.map((x, j) => (j === i ? { ...x, multiplier } : x)) })} />
                      <Button size="icon" variant="ghost" onClick={() => f.change({ multipliers: v.multipliers.filter((_, j) => j !== i) })} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => f.change({ multipliers: [...v.multipliers, { roleId: null, multiplier: 2 }] })} className={outlineBtn}><Plus className="mr-1 h-4 w-4" />Bonus ruolo</Button>
                </div>
                <div className="space-y-2">
                  <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Per canale<Info text="I messaggi in questo canale (o categoria) valgono di più. Si somma al bonus del ruolo (es. ruolo ×2 e canale ×1,5 = ×3)." /></p>
                  {(v.channelMultipliers ?? []).map((m, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="min-w-0 flex-1"><ChannelSelect value={m.channelId} types={["text", "announcement", "voice", "category"]} onChange={(channelId) => f.change({ channelMultipliers: v.channelMultipliers.map((x, j) => (j === i ? { ...x, channelId } : x)) })} /></div>
                      ×<NumberInput value={m.multiplier} min={0} max={10} step={0.5} className="w-20" onChange={(multiplier) => f.change({ channelMultipliers: v.channelMultipliers.map((x, j) => (j === i ? { ...x, multiplier } : x)) })} />
                      <Button size="icon" variant="ghost" onClick={() => f.change({ channelMultipliers: v.channelMultipliers.filter((_, j) => j !== i) })} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => f.change({ channelMultipliers: [...(v.channelMultipliers ?? []), { channelId: null, multiplier: 2 }] })} className={outlineBtn}><Plus className="mr-1 h-4 w-4" />Bonus canale</Button>
                </div>
              </div>
            </Panel>

            {/* ── ruoli premio ── */}
            <Panel title="Ruoli premio" desc="Raggiunto il livello, il bot dà il ruolo in automatico."
              right={<Button size="sm" className={primaryBtn} onClick={() => f.change({ rewards: [...v.rewards, { level: (v.rewards.at(-1)?.level ?? 0) + 5, roleId: null }] })}><Plus className="mr-1 h-4 w-4" />Premio</Button>}>
              <div className="space-y-2">
                {v.rewards.map((r, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border border-[#232838] bg-[#131620] p-3 text-sm text-[#DBDEE1]">
                    Livello <NumberInput value={r.level} min={1} max={1000} className="w-20" onChange={(level) => f.change({ rewards: v.rewards.map((x, j) => (j === i ? { ...x, level } : x)) })} />
                    <span className="text-xs text-[#6b7185]">({nf(curveTable(curve, r.level)[r.level] ?? 0)} punti)</span>
                    <div className="min-w-[180px] flex-1"><RoleSelect value={r.roleId} onChange={(roleId) => f.change({ rewards: v.rewards.map((x, j) => (j === i ? { ...x, roleId } : x)) })} /></div>
                    <Button size="icon" variant="ghost" onClick={() => f.change({ rewards: v.rewards.filter((_, j) => j !== i) })} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                {v.rewards.length === 0 && <p className="text-sm text-[#949BA4]">Nessun ruolo premio.</p>}
                <ToggleRow title="Accumula i ruoli premio" desc="Disattivato: tiene solo il ruolo del livello più alto." checked={v.stackRewards} onChange={(stackRewards) => f.change({ stackRewards })} />
              </div>
            </Panel>

            {/* ── annuncio ── */}
            <Panel title="Messaggio di livello superiore" icon={Megaphone}
              right={<Button size="sm" variant="outline" disabled={testing || v.announceMode === "none"} onClick={test} className={outlineBtn}>{testing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Invia prova</Button>}>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Dove annunciarlo"><SimpleSelect value={v.announceMode} onChange={(announceMode) => f.change({ announceMode })}
                    options={[{ value: "current", label: "Nel canale dove ha scritto" }, { value: "channel", label: "In un canale fisso" }, { value: "dm", label: "In privato" }, { value: "none", label: "Non annunciare" }]} /></Field>
                  {v.announceMode === "channel" && <Field label="Canale"><ChannelSelect value={v.announceChannelId} onChange={(announceChannelId) => f.change({ announceChannelId })} suggestName="livelli" /></Field>}
                </div>
                {v.announceMode !== "none" && (
                  <>
                    <div className="grid gap-3 md:grid-cols-2">
                      <Field label={<Label info="Esempio: 5 = annuncia solo i livelli 5, 10, 15… (i livelli con un ruolo premio vengono annunciati sempre).">Annuncia ogni</Label>}>
                        <NumberInput value={v.announceEvery ?? 1} min={1} max={100} className="w-20" suffix="livelli (1 = sempre)" onChange={(announceEvery) => f.change({ announceEvery })} />
                      </Field>
                      <Field label={<Label info="Il messaggio sparisce da solo dopo questo tempo, così il canale resta pulito. 0 = resta per sempre.">Elimina l'annuncio dopo</Label>}>
                        <NumberInput value={v.announceDeleteAfter ?? 0} min={0} max={86400} className="w-24" suffix="secondi (0 = mai)" onChange={(announceDeleteAfter) => f.change({ announceDeleteAfter })} />
                      </Field>
                      <ToggleRow title="Solo ai livelli con un premio" desc="Annuncia solo quando si vince un ruolo premio." checked={!!v.announceOnlyRewards} onChange={(announceOnlyRewards) => f.change({ announceOnlyRewards })} />
                      <ToggleRow title="Tagga la persona" desc="Spento: il nome si vede ma non arriva la notifica." checked={v.announcePing !== false} onChange={(announcePing) => f.change({ announcePing })} />
                    </div>
                    <MessageMini value={v.levelUpMessage} onChange={(levelUpMessage) => f.change({ levelUpMessage })} hint="Segnaposto extra: {level} nuovo livello · {xp} punti totali · {next} punti per il prossimo livello · {role} ruolo premio vinto (se c'è)." />
                  </>
                )}
              </div>
            </Panel>

            <Panel title="Altro">
              <ToggleRow title="Chi esce dal server perde i punti" desc="Spento: se rientra ritrova il suo livello." checked={!!v.resetOnLeave} onChange={(resetOnLeave) => f.change({ resetOnLeave })} />
            </Panel>
            <SaveBar feature={f} prepare={(x) => ({
              ...x, levelUpMessage: cleanMessage(x.levelUpMessage), rewards: x.rewards.filter((r) => r.roleId), multipliers: x.multipliers.filter((m) => m.roleId),
              channelMultipliers: (x.channelMultipliers ?? []).filter((m) => m.channelId),
              curve: { ...CURVE_DEF, ...(x.curve || {}), milestones: [...(x.curve?.milestones || [])].sort((p, q) => p.level - q.level) },
            })} />
          </div>
        );
      }}
    </FeatureGate>
  );
}

// ─────────────────────────── Economia ───────────────────────────
export function Economy() {
  const [tab, setTab] = useState("board");
  const f = useFeature("economy");
  return (
    <div className="space-y-6">
      <PageHeader icon={Coins} title="Economia" subtitle="Monete virtuali: ricompensa giornaliera, lavori, pagamenti e negozio di ruoli." />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "board", label: "Classifica", icon: Coins }, { id: "shop", label: "Negozio", icon: ShoppingCart }, { id: "settings", label: "Impostazioni", icon: Settings2 }]} />
      <FeatureGate feature={f}>
        {(v) => (
          <>
            {tab === "board" && <Richest currency={v} />}
            {tab === "shop" && <Shop f={f} v={v} />}
            {tab === "settings" && <EconomySettings f={f} v={v} />}
          </>
        )}
      </FeatureGate>
    </div>
  );
}

const money = (n, v) => `${v.currencyEmoji ? v.currencyEmoji + " " : ""}${Number(n).toLocaleString("it-IT")} ${v.currencyName}`;

function Richest({ currency }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => botGet("economy/leaderboard", { limit: 50 }).then(setData).catch(() => setData({ users: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      {data.users.length === 0 ? <Empty icon={Coins} title="Nessuno ha ancora monete">I membri usano /daily e /work per guadagnare.</Empty> : (
        <div className="divide-y divide-[#232838] overflow-hidden rounded-2xl border border-[#232838]">
          {data.users.map((u) => (
            <RankRow key={u.userId} u={u} onEdit={() => setEditing(u)}>
              <p className="text-xs text-[#949BA4]"><span className="font-semibold text-[#FEE75C]">{money(u.balance, currency)}</span> · guadagnate {Number(u.earned ?? 0).toLocaleString("it-IT")} · spese {Number(u.spent ?? 0).toLocaleString("it-IT")}</p>
            </RankRow>
          ))}
        </div>
      )}
      <EditValue open={!!editing} title={`Saldo di ${editing?.username}`} label="Saldo" suffix={currency.currencyName} value={editing?.balance ?? 0} onClose={() => setEditing(null)}
        onSave={async (balance) => { try { await botPut(`economy/users/${editing.userId}`, { balance }); toast.success("Saldo aggiornato"); setEditing(null); load(); } catch (e) { toast.error(apiError(e)); } }} />
    </div>
  );
}

function Shop({ f, v }) {
  const setItem = (i, patch) => f.change({ shop: v.shop.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  return (
    <div className="space-y-4">
      {v.shop.length === 0 && <Empty icon={ShoppingCart} title="Negozio vuoto">Aggiungi articoli: i membri li comprano con /buy.</Empty>}
      <div className="grid gap-4 md:grid-cols-2">
        {v.shop.map((item, i) => (
          <Panel key={item.id} title={item.name || "Nuovo articolo"} icon={ShoppingCart}
            right={<Button size="icon" variant="ghost" onClick={() => f.change({ shop: v.shop.filter((_, j) => j !== i) })} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>}>
            <div className="space-y-3">
              <Field label="Nome"><TextInput value={item.name} onChange={(name) => setItem(i, { name })} maxLength={80} /></Field>
              <Field label="Descrizione"><TextInput value={item.description} onChange={(description) => setItem(i, { description })} maxLength={200} /></Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Prezzo"><NumberInput value={item.price} className="w-32" suffix={v.currencyName} onChange={(price) => setItem(i, { price })} /></Field>
                <Field label="Ruolo che si ottiene"><RoleSelect value={item.roleId} onChange={(roleId) => setItem(i, { roleId })} /></Field>
              </div>
            </div>
          </Panel>
        ))}
      </div>
      <Button onClick={() => f.change({ shop: [...v.shop, { id: `item-${Date.now().toString(36)}`, name: "", description: "", price: 1000, roleId: null }] })} className={primaryBtn}><Plus className="mr-2 h-4 w-4" />Aggiungi articolo</Button>
      <SaveBar feature={f} prepare={(x) => ({ ...x, shop: x.shop.filter((s) => s.name.trim()).map((s) => ({ ...s, description: s.description || undefined })) })} />
    </div>
  );
}

function EconomySettings({ f, v }) {
  return (
    <div className="space-y-4">
      <ToggleRow title={v.enabled ? "Economia attiva" : "Economia disattivata"} desc="Abilita /daily, /work, /pay, /shop, /buy." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
      <Panel title="Valuta" icon={Coins} desc={`Esempio: ${money(1250, v)}`}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Nome"><TextInput value={v.currencyName} onChange={(currencyName) => f.change({ currencyName })} maxLength={30} /></Field>
          <Field label="Emoji"><TextInput value={v.currencyEmoji} onChange={(currencyEmoji) => f.change({ currencyEmoji })} maxLength={60} /></Field>
          <Field label="Saldo iniziale"><NumberInput value={v.startingBalance} className="w-32" onChange={(startingBalance) => f.change({ startingBalance })} /></Field>
        </div>
      </Panel>
      <Panel title="Guadagni">
        <div className="space-y-3 text-sm text-[#DBDEE1]">
          <div className="flex flex-wrap items-center gap-2">/daily dà <NumberInput value={v.dailyAmount} className="w-28" suffix="ogni 24 ore" onChange={(dailyAmount) => f.change({ dailyAmount })} /></div>
          <div className="flex flex-wrap items-center gap-2">/work dà da <NumberInput value={v.workMin} className="w-24" onChange={(workMin) => f.change({ workMin })} /> a <NumberInput value={v.workMax} className="w-24" onChange={(workMax) => f.change({ workMax })} /> ogni <NumberInput value={v.workCooldownMinutes} min={1} max={10080} className="w-20" suffix="minuti" onChange={(workCooldownMinutes) => f.change({ workCooldownMinutes })} /></div>
          <div className="flex flex-wrap items-center gap-2">Per ogni messaggio <NumberInput value={v.messageReward} max={1e6} className="w-24" suffix="(max 1 al minuto, 0 = niente)" onChange={(messageReward) => f.change({ messageReward })} /></div>
        </div>
      </Panel>
      <SaveBar feature={f} />
    </div>
  );
}
